import React, { useMemo } from 'react';
import proptypes from 'prop-types';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { RecentPurchases, RecentPurchasesTile } from '@thd-olt-component-react/recent-order-details';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { SnapshotPodPanel } from '../snapshot-tab-view/SnapshotPodPanel';
import { ViewAllOrdersFooter } from '../snapshot-tab-view/ViewAllOrdersFooter';
import { useLatestOrders } from '../snapshot-tab-view/useLatestOrders';
import { LATEST_ORDERS_EMPTY_TEXT, LATEST_ORDERS_ERROR_TEXT } from '../snapshot-tab-view/constants';

const {
  arrayOf, shape, string, number
} = proptypes;

// An order's identity: the same value the tile list is keyed by.
const getOrderId = (order) => order.orderNumbers?.[0] || order.receiptDetails || String(order.transactionId);

// Fixed impression for the "no recent orders" view (AAP1-1153 2.1). Container is the
// component-level ProAlertsAndUpdates provider, as for the cards.
const LATEST_ORDERS_NULL_IMPRESSION = {
  id: 'OrdersNull',
  name: 'YourLatestOrdersNull',
  component: 'ProAlertsAndUpdates',
  type: 'content',
  position: 1,
};

/**
 * One "Your Latest Orders" tile. RecentPurchasesTile owns its own QueryProvider and
 * per-order detail queries, so this wrapper only supplies the order plus the shared
 * customerInfo (see useLatestOrders) and isolates a failing tile from the rest of the tab.
 * Not exported: LatestOrdersCard is the only thing that renders one.
 *
 * No impression is registered here: the tile already tracks itself (RecentPurchasesTile), and
 * it can't be turned off, so a second per-order impression would double-count each card.
 *
 * `index` drives the tile's own lazy-loading threshold — tiles past the second defer
 * their status and item lookups, which suits the Filmstrip.
 *
 * RecentPurchasesTile's own root has a border but no background class and no className
 * prop to add one, so on its own it shows through to the page's ambient gray instead of
 * the white surface every other pod in this Filmstrip gets. The wrapping div supplies
 * just that background — the tile keeps its own border/padding/radius.
 *
 * Filmstrip's slides don't stretch to a common row height on their own, so tiles with
 * less content (fewer status/edit-icon lines) end up visibly shorter than their
 * neighbors — and the tile's own bordered box only grows to its own content height
 * regardless of the wrapper's, since it has no height styling of its own to stretch.
 * The wrapper is a flex container (default row) so its one child stretches to fill it
 * via the default align-items: stretch — the only lever available since the tile takes
 * no className/style to size itself. minHeight matches PodSkeleton's, so the row is the
 * same height whether tiles are loading or loaded.
 */
const RecentPurchasesTileCard = ({ order, customerInfo, index = 0 }) => {
  return (
    <ErrorBoundary name="dashboard-snapshot-recent-purchases-tile">
      <div
        className="sui-bg-primary sui-rounded-md sui-flex sui-h-full"
        style={{ minHeight: '25rem' }}
      >
        <RecentPurchasesTile
          order={order}
          index={index}
          customerInfo={customerInfo}
          isDashboardSnapshot
        />
      </div>
    </ErrorBoundary>
  );
};

RecentPurchasesTileCard.propTypes = {
  order: shape({
    POJobName: string,
    detailsSource: string,
    orderNumber: string,
    orderNumbers: arrayOf(string),
    orderOrigin: string,
    receiptDetails: string,
    registerNumber: number,
    salesDate: string,
    storeNumber: number,
    totalAmount: number,
    transactionId: number,
    transactionType: string,
  }).isRequired,
  customerInfo: shape({
    userId: string,
    customerAccountId: string,
    sessionId: string,
    channelId: string,
  }).isRequired,
  index: number,
};

/**
 * Not exported: the hook/render body lives here so LatestOrdersCard can wrap it in an
 * ErrorBoundary, matching ProjectsCard/QuotesCard/UserListsCard — each of which bounds
 * its own hook-driven content the same way. Per-tile boundaries above only isolate a
 * single RecentPurchasesTile; this one covers the order-list query and panel itself.
 */
const LatestOrdersPanel = () => {
  const { svocID, userID } = useThdCustomer() || {};
  const {
    orders, count, loading, error, customerInfo
  } = useLatestOrders(userID, svocID);

  const orderTiles = useMemo(
    () => orders.map((order, index) => (
      <RecentPurchasesTileCard
        key={getOrderId(order)}
        order={order}
        index={index}
        customerInfo={customerInfo}
      />
    )),
    [orders, customerInfo]
  );

  // Matches Projects/Lists/Quotes: only shown once real orders have loaded, not during
  // loading/error/empty, which SnapshotPodPanel already covers with its own states.
  const showViewAllOrders = !loading && !error && orders.length > 0;

  return (
    <>
      {/* Keyed by tile count so the Filmstrip remounts and recomputes slide widths when the
          number of orders changes — Filmstrip only measures on mount/resize, not on children
          changes, which otherwise leaves late-arriving slides at width:auto. */}
      <SnapshotPodPanel
        key={`latestOrders-${orderTiles.length}`}
        loading={loading}
        emptyText={error ? LATEST_ORDERS_ERROR_TEXT : LATEST_ORDERS_EMPTY_TEXT}
        // A failed query shows the same box with the error text — that isn't the null view.
        emptyImpression={error ? undefined : LATEST_ORDERS_NULL_IMPRESSION}
      >
        {orderTiles}
      </SnapshotPodPanel>
      {showViewAllOrders && (
        <ViewAllOrdersFooter count={count ?? orders.length} testId="latest-orders-view-all" />
      )}
    </>
  );
};

/**
 * The "Your Latest Orders" tab. RecentPurchasesTile renders a single order, so — unlike
 * the other tabs, which hand their slot to a shared component that owns everything —
 * this card owns the order-list query (see useLatestOrders) and maps one tile per order
 * into the shared SnapshotPodPanel (Filmstrip + skeleton/empty states). Only mounted
 * while this tab is active, same as the other three.
 */
export const LatestOrdersCard = () => (
  <ErrorBoundary name="dashboard-snapshot-latest-orders">
    <LatestOrdersPanel />
  </ErrorBoundary>
);

LatestOrdersCard.displayName = 'LatestOrdersCard';
// RecentPurchases carries the `purchaseHistory` model this card queries directly, plus the
// per-order models RecentPurchasesTile needs. recent-order-details doesn't export the
// purchaseHistory query component on its own (it exports the four order-detail ones), and
// redeclaring the PurchaseHistoryRequest shape here would duplicate that schema contract.
LatestOrdersCard.dataModel = RecentPurchases.dataModel;
